import React from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import StarRating from './StarRating';
import { band as bandDict, bandTextClass, departmentSuggestionStatus } from '@/lib/dict';
import type { Band, SuggestedDepartment } from '@/lib/api/types';

/**
 * Балл и звёзды одной колонкой.
 *
 * Раньше это были две колонки, и в плотной таблице они читались как два
 * несвязанных числа. Вместе видно то, ради чего их и смотрят рядом:
 * совпадает ли арифметика правил с суждением человека, послушавшего
 * разговор.
 *
 * **Складывать их по-прежнему нельзя.** Балл — арифметика правил,
 * объяснимая построчно; звёзды — оценка супервизора. Среднее из них ничего
 * не значит: в отчёте по оператору станет непонятно, что упало —
 * проверяемые действия или отношение руководителя. Поэтому одна колонка, но
 * два отдельных значения, а не сумма.
 *
 * Балл — текст с цветом полосы, без бейджа: фон в каждой строке тянул на
 * себя внимание, а видеть надо строки с плохим баллом. Цвет всё равно от
 * сервера — провал критического правила красит красным балл 80.
 *
 * Третьей строкой — предложенный моделью отдел, если он есть. Это тоже не
 * замена баллу: 85 в бейдже посчитаны по **чужому** чек-листу, и сортировка
 * с показателями сотрудника считаются по первому числу.
 */
export default function ScoreCell({
  score,
  band,
  criticalViolation = false,
  manualRating,
  suggestedDepartment,
}: {
  score: number | null;
  band: Band;
  criticalViolation?: boolean;
  manualRating: number | null;
  /** Модель отнесла разговор к другому отделу. Пусто у подавляющего большинства. */
  suggestedDepartment?: SuggestedDepartment | null;
}) {
  const scored = score !== null && band !== '';

  return (
    /* По центру: балл и звёзды — узкая колонка из двух строк разной ширины,
       и по левому краю они выглядели лесенкой. */
    <div className="flex flex-col items-center gap-0.5">
      {scored ? (
        <div
          className={`flex items-center gap-1 text-sm font-semibold tabular-nums ${bandTextClass[band]}`}
          title={
            criticalViolation
              ? `${bandDict[band].label} — провалено критическое правило`
              : `${bandDict[band].label} — ${Math.round(score)} из 100`
          }
        >
          {criticalViolation && <AlertTriangle size={11} className="flex-shrink-0" />}
          {Math.round(score)}
        </div>
      ) : (
        /* Прочерк, а не ноль: ни одно правило не смогло примениться. */
        <div className="text-xs text-slate-300" title={bandDict[''].hint}>
          —
        </div>
      )}

      {/* Звёзды под баллом — второе измерение, не замена первому. */}
      {manualRating !== null && <StarRating value={manualRating} size={11} readOnly />}

      {/* Отклонённое не показываем вовсе: руководитель уже сказал, что это
          разговор своего отдела, и напоминание об отвергнутой догадке только
          мешает читать список. Нерассмотренное заметнее решённого — это
          очередь, которую разбирают. */}
      {suggestedDepartment && suggestedDepartment.status !== 'rejected' && (
        <div
          className={`inline-flex items-center gap-0.5 text-[9px] leading-none px-1.5 py-0.5 rounded-full border max-w-28 ${
            suggestedDepartment.status === 'pending'
              ? 'border-blue-200 bg-blue-50 text-blue-700'
              : 'border-slate-200 text-slate-400'
          }`}
          title={`${departmentSuggestionStatus[suggestedDepartment.status].label}: похоже на работу отдела «${
            suggestedDepartment.unitName
          }». Балл посчитан по его чек-листу и оценку сотрудника не меняет`}
        >
          <ArrowRight size={9} className="flex-shrink-0" />
          <span className="truncate">{suggestedDepartment.unitName}</span>
          {suggestedDepartment.score !== null && (
            <span className="tabular-nums">{Math.round(suggestedDepartment.score)}</span>
          )}
        </div>
      )}
    </div>
  );
}
